import {
  ENROLLMENT_COHORT_STATUS_KEYS,
  type EnrollmentCohortStatusKey,
  type EnrollmentCohortStatusOptions,
  type EnrollmentCohortStatusResponse,
} from "@t3tools/contracts";
import type { ChartDataset } from "chart.js";

export const cohortStatusStyles: Record<
  EnrollmentCohortStatusKey,
  { label: string; color: string; group: "New" | "GUIDE" }
> = {
  new_referral: { label: "New referral", color: "#3976BD", group: "New" },
  enrollment_scheduled: { label: "Enrollment scheduled", color: "#F0CD49", group: "New" },
  awaiting_additional_referral_information: {
    label: "Awaiting additional referral information",
    color: "#8064B5",
    group: "New",
  },
  additional_call_to_be_scheduled: {
    label: "Additional call to be scheduled",
    color: "#A9CF56",
    group: "New",
  },
  enrollment_missed: { label: "Enrollment missed", color: "#EA853A", group: "New" },
  interested: { label: "Interested", color: "#76CDE1", group: "New" },
  not_currently_interested: { label: "Not currently interested", color: "#D67FB4", group: "New" },
  unresponsive_to_multiple_calls: {
    label: "Unresponsive to multiple calls",
    color: "#AEB5BF",
    group: "New",
  },
  explicitly_not_interested: { label: "Explicitly not interested", color: "#9D3E59", group: "New" },
  do_not_call: { label: "Do not call", color: "#422A68", group: "New" },
  duplicate_account: { label: "Duplicate account", color: "#ECE2C4", group: "New" },
  invalid_phone_number: { label: "Invalid phone number", color: "#A8A137", group: "New" },
  no_longer_in_care_with_provider: {
    label: "No longer in care with provider",
    color: "#8B5E3B",
    group: "New",
  },
  unrecorded: { label: "No enrollment status recorded", color: "#E3EAF1", group: "New" },
  assessed: { label: "Assessed", color: "#23395B", group: "GUIDE" },
  submitted: { label: "Submitted", color: "#B25C20", group: "GUIDE" },
  pending: { label: "Pending", color: "#9B8318", group: "GUIDE" },
  review: { label: "Review", color: "#F0A98D", group: "GUIDE" },
  aligned: { label: "Aligned", color: "#178D7C", group: "GUIDE" },
  unaligned: { label: "Unaligned", color: "#795B4D", group: "GUIDE" },
  not_eligible: { label: "Not eligible", color: "#4C5668", group: "GUIDE" },
};

export function cohortStatusFilters(
  options: EnrollmentCohortStatusOptions | undefined,
  selected: string[] | null,
  selectedMonth: string,
) {
  const clinics = options?.clinics ?? [];
  const defaultClinic = clinics.find((c) => c.name === "Memory Treatment Centers") ?? clinics[0];
  const ids = (selected ?? (defaultClinic ? [defaultClinic.id] : []))
    .filter((id) => clinics.some((c) => c.id === id))
    .sort();
  const months = [...new Set(clinics.flatMap((c) => c.months))].sort().reverse();
  const currentMonth = options
    ? new Date(Date.parse(`${options.throughDate}T00:00:00Z`) + 86_400_000)
        .toISOString()
        .slice(0, 7) + "-01"
    : "";
  const month = months.includes(selectedMonth)
    ? selectedMonth
    : (months.find((m) => m < currentMonth) ?? months[0] ?? "");
  return { ids, months, month };
}

export function cohortStatusDatasets(data: EnrollmentCohortStatusResponse): {
  keys: EnrollmentCohortStatusKey[];
  datasets: ChartDataset<"line", { x: number; y: number }[]>[];
} {
  const present = new Set(data.points.flatMap((point) => point.counts.map((item) => item.status)));
  const keys = ENROLLMENT_COHORT_STATUS_KEYS.filter((key) => present.has(key));
  return {
    keys,
    datasets: keys.map((key) => {
      const style = cohortStatusStyles[key];
      const points = data.points.map((point) => ({
        x: point.day,
        y: (100 * (point.counts.find((item) => item.status === key)?.count ?? 0)) / point.observed,
      }));
      const last = points.at(-1);
      if (last) points.push({ x: last.x + 1, y: last.y });
      return {
        label: style.group === "New" ? `New · ${style.label}` : style.label,
        data: points,
        backgroundColor: style.color,
        borderColor: style.color,
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: true,
        stepped: "before",
        stack: "status",
      };
    }),
  };
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function count(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function date(value: unknown): value is string {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
export function isCohortStatusOptions(value: unknown): value is EnrollmentCohortStatusOptions {
  return (
    record(value) &&
    value.source === "live" &&
    date(value.asOf) &&
    date(value.throughDate) &&
    Array.isArray(value.clinics) &&
    value.clinics.every(
      (clinic: unknown) =>
        record(clinic) &&
        typeof clinic.id === "string" &&
        typeof clinic.name === "string" &&
        Array.isArray(clinic.months) &&
        clinic.months.every(
          (m: unknown) => typeof m === "string" && /^\d{4}-(0[1-9]|1[0-2])-01$/.test(m),
        ),
    )
  );
}
export function isCohortStatusResponse(value: unknown): value is EnrollmentCohortStatusResponse {
  if (
    !record(value) ||
    value.source !== "live" ||
    !date(value.asOf) ||
    !date(value.throughDate) ||
    typeof value.month !== "string" ||
    !/^\d{4}-(0[1-9]|1[0-2])-01$/.test(value.month) ||
    !Array.isArray(value.clinicIds) ||
    !value.clinicIds.every((id: unknown) => typeof id === "string") ||
    !count(value.cohortSize) ||
    !(value.fullyObservedDays === null || count(value.fullyObservedDays)) ||
    !Array.isArray(value.points)
  )
    return false;
  const size = value.cohortSize;
  return (
    value.points.every((point: unknown, index: number) => {
      if (
        !record(point) ||
        point.day !== index ||
        !count(point.observed) ||
        point.observed === 0 ||
        point.observed > size ||
        !Array.isArray(point.counts)
      )
        return false;
      let sum = 0;
      const seen = new Set();
      for (const item of point.counts) {
        if (
          !record(item) ||
          !count(item.count) ||
          item.count === 0 ||
          !ENROLLMENT_COHORT_STATUS_KEYS.some((key) => key === item.status) ||
          seen.has(item.status)
        )
          return false;
        sum += item.count;
        seen.add(item.status);
      }
      return sum === point.observed;
    }) &&
    (size === 0
      ? value.points.length === 0 && value.fullyObservedDays === null
      : value.points.length > 0 && value.fullyObservedDays !== null)
  );
}
